// Product page loaders - these will be loaded dynamically with the pages
// These wrapper functions import and execute the actual loaders

import {UserService} from '../service/UserService';
import {generateSessionIdIfNotExists} from '../util/TrackingUtil';
import {TraceService} from '../service/TraceService';
import {TraceType} from '../model/user/general/Trace';
import {User} from '../model/user/User';
import {redirect} from 'react-router-dom';
import {getRawIdTokenAsString} from '../util/AuthUtil';

export const productRootLoader = async ({request}: { request: Request }): Promise<User | Response | null> => {
    generateSessionIdIfNotExists();
    TraceService.addTrace(TraceType.PageLoad, undefined, request.url);

    try {
        return await UserService.whoAmI();
    } catch (error: any) {
        const status = Number(error?.httpsStatus);
        if (status === 401 || status === 403) {
            // On hard refresh Cognito Amplify may not have hydrated when the loader fires;
            // the first whoAmI then goes out with `Bearer PUBLIC` and is rejected. If a token
            // is now available, retry once before assuming the session is gone.
            const token = await getRawIdTokenAsString();
            if (token) {
                UserService.invalidateWhoAmICache();
                try {
                    return await UserService.whoAmI();
                } catch (retryError: any) {
                    const retryStatus = Number(retryError?.httpsStatus);
                    if (retryStatus === 401 || retryStatus === 403) {
                        return redirect('/logowanie');
                    }
                    return null;
                }
            }
            return redirect('/logowanie');
        }
        return null;
    }
};

export const studentDashboardLoader = async (args: any) => {
    const module = await import("../pages/user/products/StudentDashboardPage");
    return module.loader ? await (module.loader as any)(args) : null;
};

export const kedLoader = async (args: any) => {
    const module = await import("../pages/user/learning-course/LearningCourseDashboardPage");
    return module.kedLoader ? await (module.kedLoader as any)(args) : null;
};

export const kedV2Loader = async (args: any) => {
    const module = await import("../pages/user/learning-course/LearningCourseDashboardPage");
    return module.kedV2Loader ? await (module.kedV2Loader as any)(args) : null;
};

export const kedV2RankingsLoader = async (args: any) => {
    const module = await import("../pages/user/learning-course/CourseRankingsPage");
    return module.kedV2RankingsLoader ? await (module.kedV2RankingsLoader as any)(args) : null;
};

export const pedLoader = async (args: any) => {
    const module = await import("../pages/user/learning-course/LearningCourseDashboardPage");
    return module.pedLoader ? await (module.pedLoader as any)(args) : null;
};

export const znLoader = async (args: any) => {
    const module = await import("../pages/user/learning-course/LearningCourseDashboardPage");
    return module.znLoader ? await (module.znLoader as any)(args) : null;
};

export const spcLoader = async (args: any) => {
    const module = await import("../pages/user/learning-course/LearningCourseDashboardPage");
    return module.spcLoader ? await (module.spcLoader as any)(args) : null;
};

export const resourceCourseLoader = async (args: any) => {
    const module = await import("../pages/user/learning-course/ResourceCourseDashboardPage");
    return module.resourceCourseLoader ? await (module.resourceCourseLoader as any)(args) : null;
};

export const communityPageLoader = async (args: any) => {
    const module = await import("../pages/user/products/CommunityPage");
    return module.loader ? await (module.loader as any)(args) : null;
};

export const communityWeeklyMeetingsLoader = async (args: any) => {
    const module = await import("../pages/user/products/CommunityWeeklyMeetingsPage");
    return module.loader ? await (module.loader as any)(args) : null;
};

export const communityKedMeetingRecordingsLoader = async (args: any) => {
    const module = await import("../pages/user/products/CommunityKedMeetingRecordingsPage");
    return module.loader ? await (module.loader as any)(args) : null;
};

export const communityKedMeetingRecordingDetailLoader = async (args: any) => {
    const module = await import("../pages/user/products/CommunityKedMeetingRecordingDetailPage");
    return module.loader ? await (module.loader as any)(args) : null;
};

export const communityIntroMeetingLoader = async (args: any) => {
    const module = await import("../pages/user/products/CommunityIntroMeetingPage");
    return module.loader ? await (module.loader as any)(args) : null;
};

export const plannerLoader = async (args: any) => {
    const module = await import("../pages/user/products/PlannerPage");
    return module.loader ? await (module.loader as any)(args) : null;
};


export const mainLearningPlanLoader = async (args: any) => {
    const module = await import("../pages/user/products/MainLearningPlanPage");
    return module.loader ? await (module.loader as any)(args) : null;
};


export const parentManagementLoader = async (args: any) => {
    const module = await import("../pages/user/products/ParentManagementPage");
    return module.parentManagementLoader ? await (module.parentManagementLoader as any)(args) : null;
};

export const flashcardsLoader = async (args: any) => {
    const module = await import("../pages/user/products/FlashcardsPage");
    return module.loader ? await (module.loader as any)(args) : null;
};

export const flashcardsAction = async (args: any) => {
    const module = await import("../pages/user/products/FlashcardsPage");
    return module.action ? await (module.action as any)(args) : null;
};

export const learningPlanLoader = async (args: any) => {
    const module = await import("../pages/user/products/LearningPlanPage");
    return module.loader ? await (module.loader as any)(args) : null;
};

export const learningPlanAction = async (args: any) => {
    const module = await import("../pages/user/products/LearningPlanPage");
    return module.action ? await (module.action as any)(args) : null;
};

export const learningPlanExerciseLoader = async (args: any) => {
    const module = await import("../pages/user/products/LearningPlanExercisePage");
    return module.loader ? await (module.loader as any)(args) : null;
};

// Exercise page loaders and actions
export const exercisesLoader = async (args: any) => {
    const module = await import("../pages/exercise/ExercisesPage");
    return module.loader ? await (module.loader as any)(args) : null;
};

export const exerciseAction = async (args: any) => {
    const module = await import("../pages/exercise/ExercisesPage");
    return module.action ? await (module.action as any)(args) : null;
};

export const exerciseViewLoader = async (args: any) => {
    const module = await import("../pages/exercise/ExerciseViewPage");
    return module.loader ? await (module.loader as any)(args) : null;
};

export const exerciseViewAction = async (args: any) => {
    const module = await import("../pages/exercise/ExerciseViewPage");
    return module.action ? await (module.action as any)(args) : null;
};

// Statistics page loader
export const statisticsLoader = async (args: any) => {
    const module = await import("../pages/user/statistics/StatisticsPage");
    return module.statisticsLoader ? await (module.statisticsLoader as any)(args) : null;
};

// Rankings page loader
export const rankingsLoader = async (args: any) => {
    const module = await import("../pages/user/rankings/RankingsPage");
    return module.rankingsLoader ? await (module.rankingsLoader as any)(args) : null;
};

export const rankingsHistoryLoader = async (args: any) => {
    const module = await import("../pages/user/rankings/RankingHistoryPage");
    return module.rankingHistoryLoader ? await (module.rankingHistoryLoader as any)(args) : null;
};
